/*eslint-disable*/
import { jwtDecode } from 'jwt-decode';
import { useRouter } from 'next/router';
import { createContext, useEffect, useRef, useState } from 'react';
import { useSessionStore } from 'store/session';
import { useUserStore } from 'store/user';

import {
  checkTokenExpiration,
  decodeJWT,
  getQueryParametersAndRemove,
} from 'lib/helper';
import { getAccessToken } from 'lib/api/getAccessToken';
import { useDepositAddresses } from 'lib/hooks/useDepositAddresses';
import { useCheckContractDeployed } from 'lib/hooks/useCheckContractDeployed';
import { useContractStore } from 'store/contract';

type GlobalProps = {
  handleLogout: () => void;
};

export const GlobalContext = createContext<GlobalProps | undefined>(undefined);

export const GlobalContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const router = useRouter();
  const currentPath = router.asPath;
  const { session, clearSession, setSession } = useSessionStore();
  const {
    isAuthenticated,
    setIsAuthenticated,
    setUserRole,
    setDepositAddresses,
  } = useUserStore();
  const [delayCompleted, setDelayCompleted] = useState(false);
  const { data } = useDepositAddresses();
  const { data: checkContracted } = useCheckContractDeployed();
  const { setShouldShowContract } = useContractStore();
  const { userRole } = useUserStore();
  const expirationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = () => {
    setIsAuthenticated(false);
    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }
    clearSession();
    setUserRole('');
    router.push('/login');
  };

  useEffect(() => {
    if (userRole === 'USER') {
      setShouldShowContract(checkContracted?.data);
    }

    if (userRole === 'ADMIN') {
      setShouldShowContract(true);
    }
  }, [checkContracted, userRole]);

  useEffect(() => {
    if (data?.length > 0) {
      setDepositAddresses(data || []);
    }
  }, [data]);

  const handleRecivingToken = async () => {
    try {
      const response = await getAccessToken(router.query.code as string);
      const access_token = response.payload.response;

      if (access_token) {
        return access_token;
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (session && Number(jwtDecode(session).exp) > Number(new Date()) / 1000) {
      setIsAuthenticated(true);
      setUserRole(session);
    } else {
      setIsAuthenticated(false);
      setUserRole('');
    }

    if (session && Number(jwtDecode(session).exp) < Number(new Date()) / 1000) {
      handleLogout();
    }
  }, [router.pathname, session, router.query]);

  useEffect(() => {
    const initToken = async () => {
      const { code, state } = getQueryParametersAndRemove();
      if (code && state) {
        try {
          // setIsLoading(true);
          const jwt = await handleRecivingToken();
          if (jwt !== null) {
            const decodedToken = decodeJWT(jwt as string);

            if (decodedToken?.exp) {
              setSession(jwt as string);
              const timeUntilExpiration = decodedToken?.exp * 1000 - Date.now();
              // Store timer reference
              expirationTimerRef.current = setTimeout(() => {
                if (checkTokenExpiration(decodedToken)) {
                  handleLogout();
                }
              }, timeUntilExpiration);
            } else {
              console.error(
                'Decoded token or its expiration time is undefined.'
              );
            }
          } else {
            // setOnError("Internal Server Error! Please Try Again");
          }
        } catch (error) {
          // setOnError("Internal Server Error! Please Try Again");
        } finally {
          // setIsLoading(false);
        }
      }
    };

    if (!isAuthenticated) {
      initToken();
    }
  }, [router, isAuthenticated]);

  useEffect(() => {
    // TODO: resolve race condition issue
    const timer = setTimeout(() => {
      setDelayCompleted(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  useEffect(() => {
    if (
      delayCompleted &&
      !isAuthenticated &&
      !router?.pathname?.includes('/login')
    ) {
      router.push('/login');
    }
  }, [delayCompleted, isAuthenticated, router]);

  return (
    <GlobalContext.Provider
      value={{
        handleLogout,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
